import { stringify } from 'qs';

export interface IHeaders { [key: string]: string }
export interface IQuery { [key: string]: string | number | boolean }
export type IResponseCallback = (response: Response) => void;

const HEADERS: IHeaders = { Accept: 'application/json, text/plain, */*' };
let responseCallback: IResponseCallback;

export default class RestClient {
  /**
   * update headers of all providers
   * @param headers request's additional headers
   * @return void
   */
  public static setHeaders(headers: IHeaders): void {
    restInstances.forEach((instance: RestClient): void => {
      instance.setHeaders(headers);
    });
  }

  /**
   * update request's params object of all providers
   * @param requestParams request's params object
   * @return void
   */
  public static setRequestParams(requestParams: RequestInit): void {
    restInstances.forEach((instance: RestClient): void => {
      instance.setRequestParams(requestParams);
    });
  }

  /**
   * set callback for every response of all providers
   * @param fn callback
   * @return void
   */
  public static setResponseCallback(fn: IResponseCallback): void {
    responseCallback = fn;
  }

  private readonly path: string;
  private headers: IHeaders;
  private requestParams: RequestInit;
  private readonly restPath: string;

  /**
   * create new RestClient instance - 'provider'
   * @param path provider rest point
   * @param headers headers object
   * @param restPath endpoint for all requests
   * @param requestParams request's params object
   * @param responseCallback calls on every response
   */
  public constructor(
    path: string,
    headers: IHeaders = {},
    restPath: string = '/api/rest/',
    requestParams: RequestInit = {}
  ) {
    this.path = path;
    this.restPath = restPath;
    this.headers = { ...HEADERS, ...headers };
    this.requestParams = requestParams;
    restInstances.push(this);
  }

  /**
   * update headers of current provider
   * @param headers updating headers
   * @return void
   */
  public setHeaders(headers: IHeaders): void {
    this.headers = { ...this.headers, ...headers };
  }

  /**
   * update requests params of current provider
   * @param requestParams request's params object
   * @return void
   */
  public setRequestParams(requestParams: RequestInit): void {
    this.requestParams = { ...this.requestParams, ...requestParams };
  }

  /**
   * GET-request
   * @param path request's path, relative to provider's path
   * @param query request's query params
   * @param headers request's additional headers
   * @param requestParams request's params object
   * @return Promise<R>
   */
  public get<R>(
    path: string,
    query: IQuery = {},
    headers: IHeaders | null = null,
    requestParams: RequestInit = {}
  ): Promise<R> {
    const queryKeys = Object.keys(query);
    const fullPath = path + (queryKeys.length ? `?${stringify(query)}` : '');
    return this.request<null, R>('GET', fullPath, null, headers, requestParams);
  }

  /**
   * POST-request
   * @param path request's path, relative to provider's path
   * @param body request's body
   * @param headers request's additional headers
   * @param requestParams request's params object
   * @return Promise<R>
   */
  public post<B, R>(
    path: string,
    body: B,
    headers: IHeaders | null = null,
    requestParams: RequestInit = {}
  ): Promise<R> {
    return this.request<B, R>('POST', path, body, headers, requestParams);
  }

  /**
   * PUT-request
   * @param path request's path, relative to provider's path
   * @param body request's body
   * @param headers request's additional headers
   * @param requestParams request's params object
   * @return Promise<R>
   */
  public put<B, R>(
    path: string,
    body: B,
    headers: IHeaders | null = null,
    requestParams: RequestInit = {}
  ): Promise<R> {
    return this.request<B, R>('PUT', path, body, headers, requestParams);
  }

  /**
   * PATCH-request
   * @param path request's path, relative to provider's path
   * @param body request's body
   * @param headers request's additional headers
   * @param requestParams request's params object
   * @return Promise<R>
   */
  public patch<B, R>(
    path: string,
    body: B,
    headers: IHeaders | null = null,
    requestParams: RequestInit = {}
  ): Promise<R> {
    return this.request<B, R>('PATCH', path, body, headers, requestParams);
  }

  /**
   * DELETE-request
   * @param path request's path, relative to provider's path
   * @param headers request's additional headers
   * @param body request's body
   * @param requestParams request's params object
   * @return Promise<R>
   */
  public delete<B, R>(
    path: string,
    headers: IHeaders | null = null,
    body: B,
    requestParams: RequestInit = {}
  ): Promise<R> {
    return this.request<B, R>('DELETE', path, body, headers, requestParams);
  }

  private request<B, R>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body: B,
    headers: IHeaders | null = null,
    requestParams: RequestInit = {}
  ): Promise<R> {
    const fetchPath: string = this.restPath + this.path + '/' + path;
    const fetchParams: RequestInit = {
      ...this.requestParams,
      ...requestParams,
      headers: {
        ...this.headers,
        ...this.requestParams.headers || {},
        ...requestParams.headers || {},
        ...headers || {},
      },
      method,
    };

    if (body) {
      fetchParams.body = JSON.stringify(body);
    }
    fetchParams.credentials = 'include';

    return fetch(fetchPath, fetchParams).then((response: Response) => {
      if (typeof responseCallback === 'function') {
        responseCallback(response);
      }
      // success
      if (response.ok) {
        if (response.status === 204) {
          return null;
        }
        return response.json().catch(() => null);
      }
      // error
      return response.json().then((badResponseBody: any) => {
        if (typeof badResponseBody === 'object') {
          badResponseBody.response = response;
        }
        throw badResponseBody;
      });
    });
  }
}

const restInstances: RestClient[] = [];



// WEBPACK FOOTER //
// ./src/index.ts